import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useSEO, getServiceSchema, getBreadcrumbSchema } from '../../../utils/seo';

export default function GayrimenkulInsaatHukuku() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'Gayrimenkul ve İnşaat Hukuku Isparta Avukat Danışmanlık',
    description: 'Isparta Yıldız & Başyiğit Hukuk Bürosu gayrimenkul ve inşaat hukuku alanında tapu işlemleri, alım-satım sözleşmeleri, kira uyuşmazlıkları, kat mülkiyeti ve kat irtifakı, inşaat sözleşmeleri ve gayrimenkul davaları konularında uzman hukuki danışmanlık hizmetleri sunmaktadır.',
    keywords: 'gayrimenkul hukuku Isparta, tapu işlemleri, kira uyuşmazlıkları, inşaat hukuku, emlak avukatı',
    canonical: `${siteUrl}/services/gayrimenkul-insaat-hukuku`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getServiceSchema({
          name: 'Gayrimenkul ve İnşaat Hukuku',
          description: 'Tapu işlemleri, kira sözleşmeleri, inşaat uyuşmazlıkları ve gayrimenkul davaları konusunda profesyonel hukuki danışmanlık',
          url: `${siteUrl}/services/gayrimenkul-insaat-hukuku`
        }),
        getBreadcrumbSchema([
          { name: 'Ana Sayfa', url: siteUrl },
          { name: 'Hizmetler', url: `${siteUrl}/services` },
          { name: 'Gayrimenkul ve İnşaat Hukuku', url: `${siteUrl}/services/gayrimenkul-insaat-hukuku` }
        ])
      ]
    }
  });

  const serviceFeatures = [
    {
      title: 'Tapu İşlemleri',
      description: 'Alım-satım, bağışlama, miras ve diğer tapu işlemleri',
      icon: 'ri-file-paper-line'
    },
    {
      title: 'İnşaat Sözleşmeleri',
      description: 'Müteahhitlik, alt yüklenici ve yapım sözleşmeleri',
      icon: 'ri-hammer-line'
    },
    {
      title: 'Emlak Uyuşmazlıkları',
      description: 'Tapu iptali, tescil ve sınır uyuşmazlıkları',
      icon: 'ri-scales-line'
    },
    {
      title: 'Kiralama İşlemleri',
      description: 'Kira sözleşmeleri, tahliye davaları ve kira artışları',
      icon: 'ri-key-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/9fcbcd611852badca8fc35b274a95881.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 animate-fade-in-up">
              <Link to="/services" className="text-gray-300 hover:text-white transition-colors font-poppins font-light">
                Hizmetler
              </Link>
              <i className="ri-arrow-right-s-line mx-2 text-gray-400"></i>
              <span className="text-white font-poppins font-light">Gayrimenkul ve İnşaat Hukuku</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up font-poppins" style={{ animationDelay: '0.1s' }}>
              Gayrimenkul ve İnşaat Hukuku
            </h1>
            
            <p className="text-xl text-gray-200 leading-relaxed animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.2s' }}>
              Emlak alım-satımından inşaat projelerine kadar gayrimenkul sektörünün tüm 
              hukuki ihtiyaçlarında uzman danışmanlık ve temsil hizmetleri sunuyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 animate-fade-in-up font-poppins">
              Uzmanlık Alanlarımız
            </h2>
            <div className="w-16 h-0.5 bg-slate-900 mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.1s' }}>
              Gayrimenkul ve inşaat hukuku alanında kapsamlı hizmetler
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 animate-fade-in-up hover-lift"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 font-poppins">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed font-poppins font-light">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Content */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 animate-fade-in-up font-poppins">
            Gayrimenkul Hukuku Hizmetlerimiz
          </h2>
          
          <div className="space-y-6 text-slate-600 leading-relaxed font-poppins font-light">
            <p className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Gayrimenkul sektöründe faaliyet gösteren kişi ve kurumların karşılaştığı hukuki 
              sorunların çözümünde uzman kadromuzla hizmet veriyoruz. Emlak alım-satım işlemlerinden 
              inşaat projelerinin hukuki süreçlerine kadar geniş bir yelpazede danışmanlık sunuyoruz.
            </p>
            
            <p className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Tapu işlemlerinde güvenli alışveriş için gerekli tüm hukuki kontrollerı gerçekleştiriyor, 
              müvekkillerimizi olası riskler konusunda bilgilendiriyoruz. İnşaat sözleşmelerinin 
              hazırlanması, müzakeresi ve uygulanması süreçlerinde profesyonel destek sağlıyoruz.
            </p>
            
            <p className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              Emlak uyuşmazlıklarının çözümünde, kira hukuku konularında, kat mülkiyeti 
              uyuşmazlıklarında ve gayrimenkul değerleme süreçlerinde hukuki temsil hizmeti veriyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 animate-fade-in-up font-poppins">
            Gayrimenkul Hukuku Danışmanlığı
          </h2>
          <p className="text-lg text-gray-200 mb-8 animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.1s' }}>
            Emlak işlemlerinizde güvenli adımlar atın
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <button className="bg-white text-slate-900 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-poppins font-semibold whitespace-nowrap cursor-pointer">
              Ücretsiz Danışmanlık
            </button>
            <button className="border border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-slate-900 transition-colors font-poppins font-semibold whitespace-nowrap cursor-pointer">
              Bize Ulaşın
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
